import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ReferenceLine,
  LabelList,
  useActiveTooltipLabel,
  usePlotArea,
} from 'recharts';
import ChartDetailModal from '../../components/Dashboard/ChartDetailModal';
import DashboardAnalyticsService from '../../services/DashboardAnalyticsService';
import { useRole } from '../../contexts/RoleContext';

const ALLIANZ_BLUE = '#003781';
const MEASURED_COLOR = '#1f6fd1';
const TARGET_COLOR = '#e8743b';
const BEFORE_COLOR = '#7b8794';
const MET_COLOR = '#2e9e6b';
const OVER_COLOR = '#d64545';
const GRID_COLOR = '#eef0f3';
const TARGET_DASH = '6 4';
const BEFORE_DASH = '2 4';
// Read by screen readers, never drawn -- MUI's own visuallyHidden, kept here because
// @mui/utils is not a declared dependency.
const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

const ACTUAL_LABEL = 'Achieved';
const TARGET_LABEL = 'Target SLA';
const BEFORE_LABEL = 'SLA before automation';

// Phases that still read in median days, rather than in ticket counts with a
// line coloured by the target. None: the ticket view first tried on Solutioning
// now covers every phase. Add a phase_key to bring its old view back.
export const DAYS_VIEW_PHASES = new Set();
// The server's grace on "met" (SLA_MET_TOLERANCE_DAYS): up to an hour past the target still counts.
const MET_TOLERANCE_DAYS = 1 / 24;
// Above-target red for a target-coloured line. Darker than OVER_COLOR, which is
// too close to the orange target line it is read against (dE 10.9 vs 19.1, normal vision).
const LINE_OVER_COLOR = '#b42318';

const VARIANT_OPTIONS = [
  { key: 'soft',         label: 'Soft Bandwidth Upgrade' },
  { key: 'hard',         label: 'Hard Bandwidth Upgrade' },
  { key: 'non_standard', label: 'Non-standard Request' },
];

/** Business days for display: '—' for no value, '<0.1' rather than a misleading 0.0. */
function formatDays(value) {
  if (value === null || value === undefined) return '—';
  if (value > 0 && value < 0.05) return '<0.1';
  return Number(value.toFixed(1)).toString();
}

const RANGE_PRESETS = [
  { key: 'all',    label: 'All data' },
  { key: '6m',     label: 'Last 6 months' },
  { key: '3m',     label: 'Last 3 months' },
  { key: '30d',    label: 'Last 30 days' },
  { key: 'custom', label: 'Custom range' },
];

const GRANULARITY_LABEL = { month: 'By month', week: 'By week', day: 'By day' };
// More x-axis labels than this collide on a tile.
const MAX_AXIS_LABELS = 7;

// Phases a request type doesn't track. Soft and Hard upgrades skip Solutioning:
// the catalogue prices them, so the phase lasts seconds against a 0-day target.
const HIDDEN_PHASES = { soft: ['solutioning'], hard: ['solutioning'] };
const roundPct = (met, scored) => (scored ? Math.round((1000 * met) / scored) / 10 : null);

/**
 * The payload as the page shows it: without the phases its request type
 * doesn't track (HIDDEN_PHASES), and with every figure the server sums over
 * phases -- compliance by period, the compliance KPIs, the open-request KPIs --
 * summed again over the phases left, so no number counts a phase that is not
 * on screen. The end-to-end SLA KPIs are the tracker's own totals and stay.
 */
function shownPayload(data) {
  if (!data) return null;
  const hidden = new Set(HIDDEN_PHASES[data.variant] || []);
  if (!hidden.size) return data;
  const phases = data.phases.filter((p) => !hidden.has(p.phase_key));
  const kept = new Set(phases.map((p) => p.phase_label));
  const sum = (items, field) => items.reduce((total, item) => total + item[field], 0);
  const compliance = data.periods.map((period, i) => {
    const points = phases.map((p) => p.series[i]);
    const met = sum(points, 'met');
    const scored = sum(points, 'scored');
    return { period: period.key, met, scored, pct: roundPct(met, scored) };
  });
  const latest = [...compliance].reverse().find((c) => c.scored) || null;
  const met = sum(phases, 'met');
  const scored = sum(phases, 'scored');
  return {
    ...data,
    phases,
    // A request with no measurable phase has no phase to hide.
    rows: (data.rows || []).filter((r) => !r.phase || kept.has(r.phase)),
    compliance_by_period: compliance,
    kpis: {
      ...data.kpis,
      compliance_met: met,
      compliance_scored: scored,
      compliance_pct: roundPct(met, scored),
      latest_period: latest ? latest.period : null,
      latest_pct: latest ? latest.pct : null,
      latest_met: latest ? latest.met : 0,
      latest_scored: latest ? latest.scored : 0,
      in_flight: sum(phases, 'in_flight'),
      in_flight_over_target: sum(phases, 'in_flight_over_target'),
    },
  };
}

const pad2 = (n) => String(n).padStart(2, '0');
/** Local calendar date -> 'YYYY-MM-DD'. */
const toIsoDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
/** 'YYYY-MM-DD' -> local midnight (new Date('YYYY-MM-DD') would be UTC, a day early west of it). */
const parseIsoDate = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d, days) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
/** d plus whole months, clamped to the target month's last day -- the server's rule too. */
const addMonths = (d, months) => {
  const lastDay = new Date(d.getFullYear(), d.getMonth() + months + 1, 0).getDate();
  return new Date(d.getFullYear(), d.getMonth() + months, Math.min(d.getDate(), lastDay));
};
// The browser's date input yields '' or a full date; a 2-digit year mid-typing is still a date.
const isUsableDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s) && Number(s.slice(0, 4)) >= 2000;

/**
 * The later of the local and the UTC date. The server's day is UTC and it takes
 * up to tomorrow, so neither side of UTC gets a range that ended yesterday.
 */
const currentIsoDate = () => {
  const now = new Date();
  const local = toIsoDate(now);
  const utc = now.toISOString().slice(0, 10);
  return local > utc ? local : utc;
};

/**
 * Query params for a preset ending today. Months are counted back from tomorrow,
 * as the server counts them, so "Last 6 months" is always charted by month.
 */
function presetParams(key, today) {
  const end = toIsoDate(today);
  const tomorrow = addDays(today, 1);
  if (key === '6m') return { start_date: toIsoDate(addMonths(tomorrow, -6)), end_date: end };
  if (key === '3m') return { start_date: toIsoDate(addMonths(tomorrow, -3)), end_date: end };
  if (key === '30d') return { start_date: toIsoDate(addDays(today, -29)), end_date: end };
  return {}; // all data: the server's default range
}

const shortMonth = (d) => d.toLocaleString('en-US', { month: 'short' });
const dayMonth = (d) => `${d.getDate()} ${shortMonth(d)}`;

/** '1 Apr – 5 Oct 2026', '6–12 Jul 2026', '28 Dec 2026 – 3 Jan 2027', or one day. */
function formatDateRange(startIso, endIso) {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (startIso === endIso) return `${dayMonth(end)} ${end.getFullYear()}`;
  if (start.getFullYear() !== end.getFullYear()) {
    return `${dayMonth(start)} ${start.getFullYear()} – ${dayMonth(end)} ${end.getFullYear()}`;
  }
  if (start.getMonth() === end.getMonth()) return `${start.getDate()}–${end.getDate()} ${shortMonth(end)} ${end.getFullYear()}`;
  return `${dayMonth(start)} – ${dayMonth(end)} ${end.getFullYear()}`;
}

/**
 * Labels for one payload's periods: `tick` for the x axis ('Apr', "Apr '26",
 * '6 Jul'), `full` for readouts ('Sep 2026', 'Wed 8 Jul 2026', '6–12 Jul
 * 2026'). A month cut short by the range is labelled with its actual dates.
 * Past MAX_AXIS_LABELS periods only every nth is labelled, evenly spaced.
 */
function periodLabeller(periods, granularity) {
  const byKey = Object.fromEntries(periods.map((p) => [p.key, p]));
  const step = Math.max(1, Math.ceil(periods.length / MAX_AXIS_LABELS));
  const labelled = new Set(periods.filter((_, i) => i % step === 0).map((p) => p.key));
  const multiYear = periods.length > 0
    && periods[0].start.slice(0, 4) !== periods[periods.length - 1].end.slice(0, 4);
  const tick = (key) => {
    const p = byKey[key];
    if (!p) return key;
    if (!labelled.has(key)) return '';
    const start = parseIsoDate(p.start);
    if (granularity !== 'month') return dayMonth(start);
    return multiYear ? `${shortMonth(start)} '${String(start.getFullYear()).slice(2)}` : shortMonth(start);
  };
  const full = (key) => {
    const p = byKey[key];
    if (!p) return key;
    const start = parseIsoDate(p.start);
    if (granularity === 'day') return `${start.toLocaleString('en-US', { weekday: 'short' })} ${dayMonth(start)} ${start.getFullYear()}`;
    if (granularity === 'month') {
      const wholeMonth = start.getDate() === 1 && addDays(parseIsoDate(p.end), 1).getDate() === 1;
      if (wholeMonth) return `${shortMonth(start)} ${start.getFullYear()}`;
    }
    return formatDateRange(p.start, p.end);
  };
  return { tick, full };
}

/** Y axis from 0 with at most ~5 ticks on a 1/2/5 x 10^n step, so labels never crowd. */
function niceAxis(peak) {
  const top = peak > 0 ? peak * 1.1 : 1;
  const rough = top / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 5, 10].map((s) => s * magnitude).find((s) => s >= rough);
  const max = step * Math.ceil(top / step);
  const ticks = [];
  for (let t = 0; t <= max + 1e-9; t += step) ticks.push(Number(t.toFixed(6)));
  return { max, ticks };
}

const tileSx = {
  borderRadius: '14px',
  border: '1px solid #e3e8ef',
  boxShadow: 'none',
  p: 2,
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

// alert: the caption states something already past its target, so it is shown in red.
const KpiCard = ({ label, value, unit, caption, alert = false }) => (
  <Paper elevation={0} sx={tileSx}>
    <Typography variant="body2" sx={{ color: '#5b6573', fontWeight: 500 }}>
      {label}
    </Typography>
    <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: 0.25 }}>
      <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.2, color: '#1a1a2e' }}>
        {value}
      </Typography>
      {unit && (
        <Typography variant="body2" sx={{ color: '#5b6573' }}>
          {unit}
        </Typography>
      )}
    </Stack>
    {caption && (
      <Typography variant="caption" sx={{ color: alert ? OVER_COLOR : '#6b7280', fontWeight: alert ? 600 : 400, mt: 0.5 }}>
        {caption}
      </Typography>
    )}
  </Paper>
);

const TileHeader = ({ title, subtitle }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a1a2e', lineHeight: 1.3 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" sx={{ color: '#6b7280' }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const EmptyOverlay = ({ text }) => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
    }}
  >
    <Typography variant="body2" sx={{ color: '#6b7280', background: 'rgba(255,255,255,0.9)', px: 1.5, py: 0.5, borderRadius: 1 }}>
      {text}
    </Typography>
  </Box>
);

const LineSwatch = ({ color, dash }) => (
  <svg width="22" height="8" style={{ flexShrink: 0 }}>
    <line x1="0" y1="4" x2="22" y2="4" stroke={color} strokeWidth="2.5" strokeDasharray={dash} />
  </svg>
);

const LegendItem = ({ color, dash, swatch, label }) => (
  <Stack direction="row" alignItems="center" spacing={0.75}>
    {swatch || <LineSwatch color={color} dash={dash} />}
    <Typography variant="caption" sx={{ color: '#5b6573' }}>{label}</Typography>
  </Stack>
);

const TooltipBox = ({ title, children }) => (
  <Paper elevation={3} sx={{ px: 1.5, py: 1, minWidth: 170, borderRadius: '10px' }}>
    <Typography variant="caption" component="div" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.25 }}>{title}</Typography>
    {children}
  </Paper>
);

/** One tooltip line: swatch, label, value right-aligned. */
const TooltipRow = ({ swatch, label, value, valueColor }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
    {swatch}
    <Typography variant="caption" sx={{ color: '#374151', flex: 1 }}>{label}</Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, color: valueColor || '#1a1a2e', whiteSpace: 'nowrap' }}>{value}</Typography>
  </Stack>
);

const BoxSwatch = ({ color }) => (
  <Box sx={{ width: 10, height: 10, borderRadius: '2px', background: color, flexShrink: 0 }} />
);

const daysOrDash = (value) => (value === null || value === undefined ? '—' : `${formatDays(value)} days`);

// The phase charts report hover in their header readout, so the chart keeps no
// floating box -- except a ticket-view phase, whose readout counts tickets and
// so no longer says what a point is (PointTooltip).
const NoTooltipBox = () => null;

/**
 * One figure in a phase tile's readout: the line's swatch and name, its value
 * below. swatch null is a plain figure -- a count no line draws -- with no key.
 */
const ReadoutValue = ({ color, dash, swatch, label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Stack direction="row" alignItems="center" spacing={0.75}>
      {swatch === undefined ? <LineSwatch color={color} dash={dash} /> : swatch}
      <Typography variant="caption" noWrap sx={{ color: '#5b6573' }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', pl: swatch === null ? 0 : '28px', lineHeight: 1.35 }}>
      {value}
    </Typography>
  </Box>
);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Where every request of the type on view is, so none goes missing silently.
 * The server sorts each into in view, outside the range, or with no measurable
 * phase; the page then hides HIDDEN_PHASES, so a request in view only through
 * one of those shows nowhere. The parts always add up to the type's total.
 */
function requestCoverage(data, shown) {
  if (!data || data.requests_in_variant === undefined) return null;
  const withPhase = (rows) => new Set((rows || []).filter((r) => r.phase).map((r) => r.request_id));
  const inView = withPhase(data.rows);
  const onPage = withPhase(shown?.rows);
  const hidden = HIDDEN_PHASES[data.variant] || [];
  return {
    total: data.requests_in_variant,
    onPage: onPage.size,
    hiddenOnly: inView.size - onPage.size,
    hiddenLabels: data.phases.filter((p) => hidden.includes(p.phase_key)).map((p) => p.phase_label),
    outside: data.requests_outside_range || 0,
    unmeasured: data.requests_without_phases || 0,
    earlier: data.excluded_before_start || 0,
  };
}

/** requestCoverage() as [the type's total, the breakdown] -- one sentence on the page and in the XLSX. */
function coverageSentence(coverage, typeLabel, metricsStart) {
  if (!coverage) return ['', ''];
  const parts = [`${coverage.onPage} with a phase in this range`];
  if (coverage.hiddenOnly) {
    parts.push(`${coverage.hiddenOnly} only in ${coverage.hiddenLabels.join(' and ')}, which this type doesn't track`);
  }
  if (coverage.outside) parts.push(`${coverage.outside} with no phase in this range`);
  if (coverage.unmeasured) parts.push(`${coverage.unmeasured} with no measurable phase, listed in Data View`);
  let rest = parts.join(' · ');
  if (coverage.earlier && metricsStart) {
    rest += `. ${plural(coverage.earlier, 'test request', 'test requests')} from before ${formatDateRange(metricsStart, metricsStart)} not counted`;
  }
  // "Non-standard Request" names the type; "233 Non-standard Request requests" would say it twice.
  const noun = (typeLabel || '').replace(/ Request$/, '');
  return [plural(coverage.total, `${noun} request`, `${noun} requests`), rest];
}

/** The key for a target-coloured line: green on the left, red on the right. */
const StatusLineSwatch = () => (
  <svg width="22" height="8" style={{ flexShrink: 0 }}>
    <line x1="0" y1="4" x2="11" y2="4" stroke={MET_COLOR} strokeWidth="2.5" />
    <line x1="11" y1="4" x2="22" y2="4" stroke={LINE_OVER_COLOR} strokeWidth="2.5" />
  </svg>
);

/**
 * A point on a target-coloured line: a green circle at or below the target, a
 * red triangle above it -- shape as well as colour, so it never relies on
 * colour alone. Recharts clones it as the Line's dot with cx, cy and value.
 */
const StatusMarker = ({ cx, cy, value, threshold, active = false }) => {
  if (value === null || value === undefined || !Number.isFinite(cx) || !Number.isFinite(cy)) return null;
  const marker = { className: 'sla-status-marker', 'data-cx': cx, 'data-cy': cy, stroke: '#fff', strokeWidth: 2 };
  if (value <= threshold) {
    return <circle {...marker} data-status="below" cx={cx} cy={cy} r={active ? 6.5 : 5} fill={MET_COLOR} />;
  }
  const r = active ? 9 : 7; // circumradius; the triangle points up, as the value sits above the target
  const half = r * Math.cos(Math.PI / 6);
  const points = `${cx},${cy - r} ${cx + half},${cy + r / 2} ${cx - half},${cy + r / 2}`;
  return <polygon {...marker} data-status="above" points={points} fill={LINE_OVER_COLOR} strokeLinejoin="round" />;
};

/**
 * Paint for a line coloured by which side of a threshold it is on. A line wholly
 * on one side is one colour. Otherwise a gradient over the line's own bounding
 * box switches from red to green exactly where it crosses the threshold -- the
 * curve is linear, so that box runs from its highest point to its lowest. (A
 * flat line has an empty box, on which a gradient would not paint at all; it is
 * always wholly on one side.)
 */
function statusLinePaint(values, threshold) {
  const plotted = values.filter((v) => v !== null && v !== undefined);
  const top = plotted.length ? Math.max(...plotted) : 0;
  const bottom = plotted.length ? Math.min(...plotted) : 0;
  if (top <= threshold) return { color: MET_COLOR, offset: null };
  if (bottom > threshold) return { color: LINE_OVER_COLOR, offset: null };
  return { color: null, offset: (top - threshold) / (top - bottom) };
}

/** How completed tickets did against their targets, as the end of a sentence. */
function outcomeClause(completed, met, scored) {
  const breached = scored - met;
  const noTarget = completed - scored;
  const one = completed === 1;
  if (!scored) return one ? ', with no target' : ', none with a target';
  if (!noTarget && !breached) return one ? ', within target' : ', all within target';
  if (!noTarget && !met) return one ? ', breaching its target' : ', all breaching their target';
  return noTarget
    ? `: ${met} within target, ${breached} breached and ${noTarget} with no target`
    : `: ${met} within target and ${breached} breached`;
}

/** A period in a sentence: 'in Sep 2026', 'in the week of 7–13 Sep 2026', 'on Wed 9 Sep 2026'. */
const periodPhrase = (granularity, label) => ({ day: `on ${label}`, week: `in the week of ${label}` }[granularity] || `in ${label}`);

/**
 * The plain-words summary under a ticket-view phase: how the tickets that
 * completed the phase did against their targets -- in the whole range, or in
 * the hovered period while hovering -- and, for the whole range, how many are
 * still in the phase (on asOfLabel's day, for a range that ended in the past).
 */
function ticketSummary({ phase, shown, hoveredLabel, granularity, asOfLabel }) {
  const name = phase.phase_label;
  const when = hoveredLabel && periodPhrase(granularity, hoveredLabel);
  let done = `No ticket completed ${name} in this range.`;
  if (shown.completed) {
    const tickets = plural(shown.completed, 'ticket', 'tickets');
    const outcome = outcomeClause(shown.completed, shown.met, shown.scored);
    if (when) return `${capitalise(when)}, ${tickets} completed ${name}${outcome}.`;
    done = `${tickets} completed ${name} in this range${outcome}.`;
  } else if (when) {
    return `No ticket completed ${name} ${when}.`;
  }
  const open = phase.in_flight;
  if (!open) return done;
  const over = phase.in_flight_over_target;
  const still = asOfLabel
    ? `On ${asOfLabel}, ${plural(open, 'ticket was', 'tickets were')} still in ${name}`
    : `${plural(open, 'ticket is', 'tickets are')} still in ${name}`;
  if (phase.sla_automated_days === null || phase.sla_automated_days === undefined) return `${done} ${still}.`;
  let late = `, ${over} of them already over target`;
  if (!over) late = ', none over target yet';
  else if (over === open) late = open === 1 ? ', already over target' : ', all already over target';
  return `${done} ${still}${late}.`;
}

/** A tooltip key slot as wide as a line key, so labels line up whatever the key. */
const KeySlot = ({ children = null }) => (
  <Box sx={{ width: 22, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>{children}</Box>
);

/** A point's state as a 14px key: the green circle or the red triangle drawn on the line. */
const StatusShape = ({ status }) => (
  <svg width="14" height="14" style={{ flexShrink: 0 }}>
    {status === 'below'
      ? <circle cx="7" cy="7" r="5" fill={MET_COLOR} />
      : <polygon points="7,1 12.6,10.5 1.4,10.5" fill={LINE_OVER_COLOR} strokeLinejoin="round" />}
  </svg>
);

/** The target-coloured line and its two states, by shape as well as colour; items wrap whole on a narrow tile. */
const StatusLegend = () => (
  <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1, columnGap: 2, rowGap: 0.5 }}>
    <Stack direction="row" spacing={0.75} alignItems="center">
      <StatusLineSwatch />
      <Typography variant="caption" noWrap sx={{ color: '#5b6573' }}>{ACTUAL_LABEL} (median days)</Typography>
    </Stack>
    {[['below', 'Within target'], ['above', 'Over target']].map(([status, label]) => (
      <Stack key={status} direction="row" spacing={0.75} alignItems="center">
        <StatusShape status={status} />
        <Typography variant="caption" noWrap sx={{ color: '#5b6573' }}>{label}</Typography>
      </Stack>
    ))}
  </Stack>
);

/**
 * Hovering a ticket-view phase: what the point is (that period's median time
 * to complete the phase) against the target, and the tickets behind it. Kept
 * to a few lines so it never hides much of the chart. threshold null = no
 * target to compare against.
 */
const PointTooltip = ({ active, payload, labels, threshold, phaseLabel }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  if (!point.completed) {
    return (
      <TooltipBox title={labels.full(point.period)}>
        <Typography variant="caption" component="div" sx={{ color: '#6b7280' }}>{`No ticket completed ${phaseLabel}`}</Typography>
      </TooltipBox>
    );
  }
  const hasTarget = threshold !== null && threshold !== undefined;
  const status = hasTarget && point.median > threshold ? 'above' : 'below';
  return (
    <TooltipBox title={labels.full(point.period)}>
      <TooltipRow
        swatch={<KeySlot>{hasTarget ? <StatusShape status={status} /> : <LineSwatch color={MEASURED_COLOR} />}</KeySlot>}
        label={`${ACTUAL_LABEL} (median)`}
        value={daysOrDash(point.median)}
      />
      {hasTarget && (
        <TooltipRow swatch={<KeySlot><LineSwatch color={TARGET_COLOR} dash={TARGET_DASH} /></KeySlot>} label={TARGET_LABEL} value={daysOrDash(point.target)} />
      )}
      <TooltipRow swatch={<KeySlot />} label="Total tickets" value={point.completed} />
      <TooltipRow swatch={<KeySlot />} label="Breached" value={point.scored ? point.scored - point.met : '—'} />
      <Typography variant="caption" component="div" sx={{ color: '#6b7280', mt: 0.25 }}>Click or press Enter to list these tickets</Typography>
    </TooltipBox>
  );
};

/**
 * Renders nothing: tells the tile which point recharts has active (by mouse or
 * by the arrow keys) and where the plot area is, in the chart's own pixels.
 */
const ChartStateReporter = ({ onChange }) => {
  const label = useActiveTooltipLabel();
  const area = usePlotArea();
  useEffect(() => { onChange(label, area); }, [label, area, onChange]);
  return null;
};

/**
 * A chart whose points each open a period's list: a click on a point inside the
 * plot area, or Enter on the point the arrow keys have reached. recharts makes
 * the chart focusable and, once it has focus, reports its keyboard point for
 * clicks anywhere on it -- so a click counts only inside the plot area, a mouse
 * press never focuses the chart (that would make its first point active), and
 * Enter is taken before recharts would toggle its highlight off, so one Enter
 * always reopens a list after it closes. hasPoint(index) says whether a period
 * has anything to list.
 *
 * Returns the list state, the period to show (the mouse's, else -- while the
 * chart has keyboard focus -- the keyboard's), props for the chart's box and
 * the chart, and the reporter to render inside the chart.
 */
function usePointList({ periodKeys, periods, labels, hasPoint }) {
  const [hoverIndex, setHoverIndex] = useState(null); // the mouse, inside the plot
  const [activeKey, setActiveKey] = useState(null); // the point recharts has active
  const [focused, setFocused] = useState(false);
  // period stays while the list fades out; key starts every opening afresh.
  const [list, setList] = useState({ open: false, period: null, key: 0 });
  const chartBox = useRef(null);
  const plotArea = useRef(null);
  const onChartState = useCallback((label, area) => {
    plotArea.current = area || null;
    setActiveKey(label === undefined ? null : label);
  }, []);
  // The chart fills chartBox, so their origins match.
  const inPlot = (e) => {
    const area = plotArea.current;
    if (!area || !e || !chartBox.current || typeof e.clientX !== 'number') return false;
    const box = chartBox.current.getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    return x >= area.x && x <= area.x + area.width && y >= area.y && y <= area.y + area.height;
  };
  // A period with nothing to list opens nothing. True when a list opened.
  const openPeriod = (key) => {
    const index = periodKeys.indexOf(key);
    const bounds = periods.find((p) => p.key === key);
    if (index < 0 || !bounds || !hasPoint(index)) return false;
    setList((current) => ({
      open: true,
      period: { key, start: bounds.start, end: bounds.end, label: labels.full(key) },
      key: current.key + 1,
    }));
    return true;
  };
  const keyIndex = activeKey === null ? -1 : periodKeys.indexOf(activeKey);
  let shownIndex = hoverIndex;
  if (shownIndex === null && focused && keyIndex >= 0) shownIndex = keyIndex;
  let spoken = '';
  if (focused && hoverIndex === null && keyIndex >= 0) {
    spoken = `${labels.full(periodKeys[keyIndex])}: ${hasPoint(keyIndex) ? 'press Enter to list' : 'nothing to list'}`;
  }
  return {
    list,
    // Always mounted, so the live region announces each change of text.
    announcer: <Box component="span" role="status" aria-live="polite" sx={visuallyHidden}>{spoken}</Box>,
    closeList: () => setList((current) => ({ ...current, open: false })),
    shownIndex,
    reporter: <ChartStateReporter onChange={onChartState} />,
    boxProps: {
      ref: chartBox,
      onKeyDownCapture: (e) => {
        if (e.key === 'Enter' && activeKey !== null && openPeriod(activeKey)) e.stopPropagation();
      },
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
      onMouseDown: (e) => e.preventDefault(),
    },
    chartProps: {
      onMouseMove: (state, e) => {
        const index = state && state.isTooltipActive && inPlot(e) ? Number(state.activeTooltipIndex) : NaN;
        setHoverIndex(Number.isInteger(index) && index >= 0 && index < periodKeys.length ? index : null);
      },
      onMouseLeave: () => setHoverIndex(null),
      onClick: (state, e) => { if (state && state.isTooltipActive && inPlot(e)) openPeriod(state.activeLabel); },
      style: { cursor: hoverIndex !== null && hasPoint(hoverIndex) ? 'pointer' : 'default' },
    },
  };
}

/**
 * One phase: the actual median per completion period (month, week or day) as
 * the solid line, the automated target dashed, the pre-automation SLA dotted.
 * The readout above the chart shows the phase over the whole range, and the
 * hovered period while hovering. The two SLAs are series rather than
 * reference lines so the hovered period is marked on all three. A period with
 * no completions is a gap, never a 0.
 *
 * A phase reads in tickets (unless it is in DAYS_VIEW_PHASES): the readout
 * counts the tickets that completed it and how many breached their target (a
 * dash when none had one), the line is green below the Target SLA line and red
 * above it, a small box on hover says what the point is, and a sentence under
 * the chart sums it up. The line's legend is the page's shared one, unless the
 * tiles are mixed (showStatusLegend).
 *
 * Clicking a point -- or Enter on the point the arrow keys have reached --
 * lists the tickets that completed the phase in that period (usePointList).
 */
const PhaseTrendTile = ({ phase, periodKeys, periods, rows, labels, granularity, asOfLabel, showStatusLegend = false }) => {
  const target = phase.sla_automated_days;
  const before = phase.sla_current_days;
  const data = periodKeys.map((period, i) => ({
    period,
    median: phase.series[i].median_business_days,
    completed: phase.series[i].completed,
    met: phase.series[i].met,
    scored: phase.series[i].scored,
    target,
    before,
  }));
  const peak = Math.max(
    target || 0,
    before || 0,
    ...data.map((d) => d.median || 0),
  );
  const yAxis = niceAxis(peak);
  const hasTarget = target !== null && target !== undefined;
  const hasBefore = before !== null && before !== undefined;
  // A one-point Line draws no stroke, so a single period falls back to reference lines.
  const singlePeriod = data.length === 1;
  const ticketView = !DAYS_VIEW_PHASES.has(phase.phase_key);
  // Coloured against the Target SLA line as drawn, with the server's grace on "met".
  const statusLine = ticketView && hasTarget;
  const threshold = statusLine ? target + MET_TOLERANCE_DAYS : null;
  const paint = statusLine ? statusLinePaint(data.map((d) => d.median), threshold) : null;
  const gradientId = `sla-status-line-${phase.phase_key}`;
  let lineStroke = MEASURED_COLOR;
  if (statusLine) lineStroke = paint.offset === null ? paint.color : `url(#${gradientId})`;

  const pointList = usePointList({ periodKeys, periods, labels, hasPoint: (i) => Boolean(data[i] && data[i].completed) });
  const hovered = pointList.shownIndex !== null ? data[pointList.shownIndex] : null;
  const shown = hovered || {
    median: phase.median_business_days,
    completed: phase.completed,
    met: phase.met,
    scored: phase.scored,
  };

  return (
    <Paper elevation={0} sx={tileSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a1a2e', lineHeight: 1.3 }}>
          {phase.phase_label}
        </Typography>
        <Typography variant="caption" sx={{ color: hovered ? '#1a1a2e' : '#6b7280', fontWeight: hovered ? 700 : 400 }}>
          {hovered ? labels.full(hovered.period) : 'Whole range'}
        </Typography>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${ticketView ? 4 : 3}, auto)`, justifyContent: 'space-between', columnGap: ticketView ? 1.5 : 2, mt: 0.75, mb: 1 }}>
        {ticketView ? (
          <>
            <ReadoutValue swatch={null} label="Tickets" value={shown.completed} />
            <ReadoutValue swatch={null} label="Breached" value={shown.scored ? shown.scored - shown.met : '—'} />
          </>
        ) : (
          <ReadoutValue
            color={MEASURED_COLOR}
            label={ACTUAL_LABEL}
            value={shown.completed ? daysOrDash(shown.median) : 'none completed'}
          />
        )}
        <ReadoutValue color={TARGET_COLOR} dash={TARGET_DASH} label={TARGET_LABEL} value={daysOrDash(target)} />
        <ReadoutValue color={BEFORE_COLOR} dash={BEFORE_DASH} label={ticketView ? 'Before automation' : BEFORE_LABEL} value={daysOrDash(before)} />
      </Box>
      <Box sx={{ position: 'relative', height: 190 }} {...pointList.boxProps}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 8, right: 20, bottom: 0, left: -12 }}
            {...pointList.chartProps}
            title={`${phase.phase_label} by ${granularity}`}
            desc={`Arrow keys move between ${granularity}s; Enter lists the tickets that completed ${phase.phase_label} in one.`}
          >
            {pointList.reporter}
            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
            <XAxis dataKey="period" tickFormatter={labels.tick} interval={0} tick={{ fontSize: 12, fill: '#5b6573' }} axisLine={{ stroke: '#d0d5dd' }} tickLine={false} />
            <YAxis domain={[0, yAxis.max]} ticks={yAxis.ticks} padding={{ bottom: 6 }} tick={{ fontSize: 12, fill: '#5b6573' }} axisLine={false} tickLine={false} />
            <ChartTooltip
              content={ticketView
                ? <PointTooltip labels={labels} threshold={threshold} phaseLabel={phase.phase_label} />
                : NoTooltipBox}
              cursor={{ stroke: '#c3cad5', strokeWidth: 1 }}
              wrapperStyle={{ zIndex: 10 }}
            />
            {statusLine && paint.offset !== null && (
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset={0} stopColor={LINE_OVER_COLOR} />
                  <stop offset={paint.offset} stopColor={LINE_OVER_COLOR} />
                  <stop offset={paint.offset} stopColor={MET_COLOR} />
                  <stop offset={1} stopColor={MET_COLOR} />
                </linearGradient>
              </defs>
            )}
            {hasBefore && singlePeriod && (
              <ReferenceLine y={before} stroke={BEFORE_COLOR} strokeDasharray={BEFORE_DASH} strokeWidth={2} />
            )}
            {hasTarget && singlePeriod && (
              <ReferenceLine y={target} stroke={TARGET_COLOR} strokeDasharray={TARGET_DASH} strokeWidth={2} />
            )}
            {hasBefore && !singlePeriod && (
              <Line
                type="linear"
                dataKey="before"
                name={BEFORE_LABEL}
                stroke={BEFORE_COLOR}
                strokeDasharray={BEFORE_DASH}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: BEFORE_COLOR, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            )}
            {hasTarget && !singlePeriod && (
              <Line
                type="linear"
                dataKey="target"
                name={TARGET_LABEL}
                stroke={TARGET_COLOR}
                strokeDasharray={TARGET_DASH}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: TARGET_COLOR, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            )}
            <Line
              type="linear"
              dataKey="median"
              name={ACTUAL_LABEL}
              stroke={lineStroke}
              strokeWidth={2.5}
              connectNulls
              dot={statusLine ? <StatusMarker threshold={threshold} /> : { r: 4, strokeWidth: 2, fill: '#fff' }}
              activeDot={statusLine ? <StatusMarker threshold={threshold} active /> : { r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
        {phase.completed === 0 && (
          <EmptyOverlay text="No request completed this phase in this range" />
        )}
      </Box>
      {showStatusLegend && statusLine && phase.completed > 0 && <StatusLegend />}
      {ticketView ? (
        // Both sentences share one cell, so the cell is always as tall as the
        // whole-range one and hovering never changes the tile's height.
        <Box sx={{ display: 'grid', mt: showStatusLegend && statusLine && phase.completed > 0 ? 0.5 : 1 }}>
          <Typography
            variant="caption"
            aria-hidden={hovered ? true : undefined}
            sx={{ gridArea: '1 / 1', color: '#5b6573', visibility: hovered ? 'hidden' : 'visible' }}
          >
            {ticketSummary({ phase, shown: phase, granularity, asOfLabel })}
          </Typography>
          {hovered && (
            <Typography variant="caption" sx={{ gridArea: '1 / 1', color: '#5b6573' }}>
              {ticketSummary({ phase, shown: hovered, hoveredLabel: labels.full(hovered.period), granularity, asOfLabel })}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: '#5b6573', mt: 1 }}>
          {shown.completed} completed
          {shown.scored ? ` · ${shown.met} of ${shown.scored} within target` : ''}
          {!hovered && phase.in_flight ? ` · ${phase.in_flight} in progress` : ''}
          {!hovered && phase.in_flight_over_target ? ` (${phase.in_flight_over_target} over target)` : ''}
        </Typography>
      )}
      {pointList.announcer}
      <RequestListDialog
        open={pointList.list.open}
        phaseLabel={phase.phase_label}
        period={pointList.list.period}
        chartTarget={target}
        openKey={pointList.list.key}
        rows={rows}
        asOfLabel={asOfLabel}
        granularity={granularity}
        onClose={pointList.closeList}
      />
    </Paper>
  );
};

const ComplianceTooltip = ({ active, payload, label, labels }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <TooltipBox title={labels.full(label)}>
      {point.scored ? (
        <TooltipRow
          swatch={<BoxSwatch color={MET_COLOR} />}
          label="Within target"
          value={`${Math.round(point.pct)}% · ${point.met} of ${point.scored}`}
        />
      ) : (
        <Typography variant="caption" component="div" sx={{ color: '#6b7280' }}>
          {point.completed ? 'No completed phase had a target' : 'No phases completed'}
        </Typography>
      )}
      {point.scored > 0 && (
        <Typography variant="caption" component="div" sx={{ color: '#6b7280', mt: 0.25 }}>Click or press Enter to list these phases</Typography>
      )}
    </TooltipBox>
  );
};

const OpenRequestsTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <TooltipBox title={point.phase_label}>
      <TooltipRow swatch={<BoxSwatch color={MEASURED_COLOR} />} label="Within target" value={point.within} />
      <TooltipRow swatch={<BoxSwatch color={OVER_COLOR} />} label="Over target" value={point.over} valueColor={point.over ? OVER_COLOR : undefined} />
      {point.oldest !== null && (
        <Typography variant="caption" component="div" sx={{ color: '#6b7280', mt: 0.25 }}>
          Longest wait {formatDays(point.oldest)} days
        </Typography>
      )}
    </TooltipBox>
  );
};

// completed: phases completed in each period, scored or not (an unclassified request has no target).
// A point -- a period with something scored -- lists every phase completed in it (usePointList).
const ComplianceTile = ({ compliance, completed, labels, noun, periods, rows, asOfLabel }) => {
  const periodKeys = compliance.map((c) => c.period);
  const pointList = usePointList({ periodKeys, periods, labels, hasPoint: (i) => Boolean(compliance[i] && compliance[i].scored) });
  const data = compliance.map((c, i) => ({
    ...c,
    completed: completed[i] || 0,
    // A period with nothing to score is a gap, not 0%.
    pct: c.scored ? c.pct : null,
  }));
  const hasData = data.some((d) => d.pct !== null);
  return (
    <Paper elevation={0} sx={tileSx}>
      <TileHeader title="SLA compliance % over time" subtitle={`Phases completed each ${noun} within their target`} />
      <Box sx={{ position: 'relative', height: 190 }} {...pointList.boxProps}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 8, right: 20, bottom: 0, left: -12 }}
            {...pointList.chartProps}
            title={`SLA compliance by ${noun}`}
            desc={`Arrow keys move between ${noun}s; Enter lists the phases completed in one.`}
          >
            {pointList.reporter}
            <CartesianGrid stroke={GRID_COLOR} vertical={false} />
            <XAxis dataKey="period" tickFormatter={labels.tick} interval={0} tick={{ fontSize: 12, fill: '#5b6573' }} axisLine={{ stroke: '#d0d5dd' }} tickLine={false} />
            <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} padding={{ top: 8, bottom: 6 }} tick={{ fontSize: 12, fill: '#5b6573' }} axisLine={false} tickLine={false} />
            <ChartTooltip
              content={<ComplianceTooltip labels={labels} />}
              filterNull={false}
              cursor={{ stroke: '#c3cad5', strokeWidth: 1 }}
              wrapperStyle={{ zIndex: 10 }}
            />
            <Area
              type="linear"
              dataKey="pct"
              stroke={MET_COLOR}
              strokeWidth={2.5}
              fill={MET_COLOR}
              fillOpacity={0.12}
              connectNulls
              dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
        {!hasData && (
          <EmptyOverlay text={data.some((d) => d.completed) ? 'No completed phase had a target in this range' : 'No phases completed in this range'} />
        )}
      </Box>
      <Typography variant="caption" sx={{ color: '#5b6573', mt: 1 }}>
        Hover a point for how many of that {noun}'s phases met their target; click it for the list
      </Typography>
      {pointList.announcer}
      <RequestListDialog
        open={pointList.list.open}
        phaseLabel={null}
        period={pointList.list.period}
        openKey={pointList.list.key}
        rows={rows}
        asOfLabel={asOfLabel}
        granularity={noun}
        onClose={pointList.closeList}
      />
    </Paper>
  );
};

// ---- Open requests: the list behind a bar, and one request's read-only view ----

/** The server marks an open phase over target with the same 1h grace as "met", so these are the bar's red part. */
const isOverTarget = (row) => row.result === 'In progress, over target';
/** A naive UTC stamp 'YYYY-MM-DDTHH:MM:SS' as '2 May 2026', or with ', 09:12 UTC'. */
const stampDate = (iso) => (iso ? formatDateRange(iso.slice(0, 10), iso.slice(0, 10)) : '—');
const stampTime = (iso) => (iso ? `${stampDate(iso)}, ${iso.slice(11, 16)} UTC` : '—');

/** '1 day', '0.5 days', '12 days' -- formatDays with the right noun. */
const daysText = (value, noun = 'day') => {
  const shown = formatDays(value);
  return `${shown} ${shown === '1' ? noun : `${noun}s`}`;
};

/** An open row against its phase target: how far over, or how much is left. */
function openStatus(row) {
  const target = row.target_business_days;
  if (target === null || target === undefined) return { color: '#9aa4b2', text: 'No target' };
  if (isOverTarget(row)) return { color: OVER_COLOR, text: `${daysText(row.business_days - target)} over` };
  return { color: MEASURED_COLOR, text: `${daysText(Math.max(0, target - row.business_days))} left` };
}

/** A completed row against its phase target: within it, or by how much it was breached. */
function completedStatus(row) {
  const target = row.target_business_days;
  if (row.result === 'No target' || target === null || target === undefined) return { color: '#9aa4b2', text: 'No target' };
  if (row.result === 'Missed') return { color: OVER_COLOR, text: `Breached by ${daysText(row.business_days - target)}` };
  return { color: MET_COLOR, text: 'Within target' };
}
const rowStatus = (row) => (row.completed ? completedStatus(row) : openStatus(row));

/** Status dot and words, the target beneath: the dot carries the chart's colour, the words stay in ink. */
const RowStatus = ({ row }) => {
  const { color, text } = rowStatus(row);
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ color: '#1a1a2e', whiteSpace: 'nowrap' }}>{text}</Typography>
      </Stack>
      {text !== 'No target' && (
        <Typography variant="caption" sx={{ color: '#6b7280', pl: '14px', whiteSpace: 'nowrap' }}>
          {`target ${daysText(row.target_business_days)}`}
        </Typography>
      )}
    </Box>
  );
};

const headCellSx = { fontWeight: 600, color: '#5b6573', background: '#f7f8fa', whiteSpace: 'nowrap' };

/** Track Requests, searched to one demand, in a new tab so the dashboard keeps its place. */
const trackRequestsUrl = (demand) => `/track-requests?request_type=${encodeURIComponent('Bandwidth Upgrade/Downgrade')}`
  + `&request_status=${encodeURIComponent(demand)}`;

/**
 * One fulfillment_updates JSON column as [{timestamp, comment}], newest first.
 * Several are often written with one stamp (submission writes three), so a tie
 * goes to the one stored later.
 */
function latestUpdates(raw, count) {
  let updates = [];
  try {
    updates = typeof raw === 'string' ? JSON.parse(raw) : (raw || []);
  } catch (err) {
    updates = [];
  }
  if (!Array.isArray(updates)) return [];
  return updates
    .map((update, index) => ({ update, index }))
    .filter(({ update }) => update && update.comment)
    .sort((a, b) => String(b.update.timestamp || '').localeCompare(String(a.update.timestamp || '')) || b.index - a.index)
    .slice(0, count)
    .map(({ update }) => update);
}

/**
 * One request, read-only: where it is and for how long, or how long it took to
 * complete the phase (from the payload, so it shows even if the request fails
 * to load), every phase it has in the range, and -- for an admin -- the
 * request's own status, key fields and latest updates. Those come from the
 * request as it is today, so a range that ended in the past says so.
 * GET /api/requests/<id> does not scope by owner (Track Requests shows a
 * requestor only their own requests), so a requestor gets the dashboard's own
 * figures and no fetch.
 */
const RequestView = ({ row, rows, asOfLabel, canFetch }) => {
  const [details, setDetails] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!canFetch) return undefined;
    let live = true;
    setDetails(null);
    setFailed(false);
    DashboardAnalyticsService.getRequest(row.request_id)
      .then((result) => { if (live) setDetails(result); })
      .catch((err) => {
        if (!live) return;
        console.error('Request details fetch failed', err);
        setFailed(true);
      });
    return () => { live = false; };
  }, [row.request_id, canFetch]);

  const timeline = rows.filter((r) => r.request_id === row.request_id);
  const target = row.target_business_days;
  let summary;
  if (row.completed) {
    let outcome = 'no target for this request type';
    if (row.result === 'Missed') outcome = `breached its target of ${daysText(target)} by ${daysText(row.business_days - target)}`;
    else if (row.result === 'Met') outcome = `within its target of ${daysText(target)}`;
    summary = `Completed ${row.phase} in ${daysText(row.business_days, 'business day')} `
      + `(${formatDateRange(row.started.slice(0, 10), row.completed.slice(0, 10))}) — ${outcome}.`;
  } else {
    const status = openStatus(row);
    summary = `In ${row.phase} for ${daysText(row.business_days, 'business day')}, since ${stampDate(row.started)}`
      + ` — ${status.text === 'No target' ? 'no target for this request type' : status.text}`
      + (asOfLabel ? `, as of ${asOfLabel}.` : '.');
  }
  const bandwidth = details && (details.current_bandwidth || details.new_bandwidth)
    ? `${details.current_bandwidth || '—'} → ${details.new_bandwidth || '—'}` : null;
  const facts = [
    ['Request type', row.request_type],
    ['Country', details?.country],
    ['City', details?.city],
    ['Site ID', details?.site_id],
    ['Bandwidth', bandwidth],
    ['Link type', details?.link_type],
    ['Links', details?.number_of_links],
    ['Current provider', details?.current_provider],
    ['Operating entity', details?.oe_name],
    ['Project manager', details?.assigned_pm],
  ].filter(([, value]) => value !== null && value !== undefined && value !== '');
  const updates = details ? latestUpdates(details.fulfillment_updates, 5) : [];

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#1a1a2e', mb: 1.5 }}>{summary}</Typography>
      {!canFetch && (
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 1.5 }}>
          Status, details and updates are shown to admins only.
        </Typography>
      )}
      {details && asOfLabel && (
        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>
          {`Status, details and updates below are as of today, not ${asOfLabel}.`}
        </Typography>
      )}
      {details && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          {[['Request state', details.request_state], ['Status', details.request_status], ['Quote approval', details.approval_status]]
            .filter(([, value]) => value)
            .map(([name, value]) => <Chip key={name} label={`${name}: ${value}`} size="small" variant="outlined" />)}
        </Stack>
      )}
      {canFetch && !details && !failed && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <CircularProgress size={16} sx={{ color: ALLIANZ_BLUE }} />
          <Typography variant="body2" sx={{ color: '#5b6573' }}>Loading the request…</Typography>
        </Stack>
      )}
      {failed && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Couldn't load this request's details. Its phases below come from the dashboard.
        </Alert>
      )}
      {facts.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, columnGap: 3, rowGap: 1, mb: 2 }}>
          {facts.map(([label, value]) => (
            <Box key={label} sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>{label}</Typography>
              <Typography variant="body2" sx={{ color: '#1a1a2e', overflowWrap: 'anywhere' }}>{String(value)}</Typography>
            </Box>
          ))}
        </Box>
      )}
      <Typography variant="subtitle2" sx={{ color: '#1a1a2e', mb: 0.5 }}>Phases in this range</Typography>
      <TableContainer sx={{ mb: 2, border: '1px solid #e3e8ef', borderRadius: '8px' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Phase', 'Started (UTC)', 'Completed (UTC)', 'Business days', 'Target', 'Result'].map((h) => (
                <TableCell key={h} sx={headCellSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {timeline.map((r) => (
              <TableRow key={`${r.phase}-${r.started}`}>
                <TableCell>{r.phase}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{stampDate(r.started)}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.completed ? stampDate(r.completed) : '—'}</TableCell>
                <TableCell>{formatDays(r.business_days)}</TableCell>
                <TableCell>{daysOrDash(r.target_business_days)}</TableCell>
                <TableCell>{r.result}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {details && (
        <>
          <Typography variant="subtitle2" sx={{ color: '#1a1a2e', mb: 0.5 }}>{asOfLabel ? 'Latest updates (today)' : 'Latest updates'}</Typography>
          {updates.length ? (
            <Stack spacing={0.75}>
              {updates.map((u, i) => (
                <Box key={`${u.timestamp}-${i}`} sx={{ borderLeft: '2px solid #e3e8ef', pl: 1.25 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>{stampTime(u.timestamp)}</Typography>
                  <Typography variant="body2" sx={{ color: '#1a1a2e', overflowWrap: 'anywhere', whiteSpace: 'pre-line' }}>{u.comment}</Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: '#6b7280' }}>No updates recorded.</Typography>
          )}
        </>
      )}
    </Box>
  );
};

/**
 * A list of requests in one phase, longest first, with a search over demand
 * number and title, a filter for its two parts and a read-only View of each:
 *   period null  -- the requests open in the phase (the Open requests bars),
 *                   split over / within target like the bar;
 *   period set   -- the requests that completed the phase in that period (a
 *                   phase chart's point: its completed / met / scored counts
 *                   are exactly these rows), split breached / within target;
 *                   with phaseLabel null, every phase completed in the period
 *                   (a compliance point), one row per phase, with a Phase column.
 * phaseLabel and period stay set while the dialog fades out; openKey changes on
 * every opening, which starts it on the whole list.
 */
const RequestListDialog = ({ open, phaseLabel, period = null, chartTarget = null, openKey, rows, asOfLabel, granularity, onClose }) => {
  const { isAdmin } = useRole();
  const [query, setQuery] = useState('');
  const [show, setShow] = useState('all'); // 'all' | 'flagged' | 'fine'
  const [viewing, setViewing] = useState(null); // the row being viewed
  const backRef = useRef(null);
  const viewButtons = useRef({});
  const lastViewed = useRef(null); // request_id to put focus back on after Back
  const headingId = useId();
  useEffect(() => {
    setQuery('');
    setShow('all');
    setViewing(null);
    lastViewed.current = null;
  }, [openKey]);
  // Focus follows the swap: View lands on Back, Back lands on the row's View button.
  useEffect(() => {
    if (viewing) {
      if (backRef.current) backRef.current.focus();
    } else if (lastViewed.current !== null && viewButtons.current[lastViewed.current]) {
      viewButtons.current[lastViewed.current].focus();
    }
  }, [viewing]);

  const allPhases = phaseLabel === null;
  const listRows = useMemo(
    () => rows
      .filter((r) => r.phase && (phaseLabel === null || r.phase === phaseLabel) && (period
        ? Boolean(r.completed) && r.completed.slice(0, 10) >= period.start && r.completed.slice(0, 10) <= period.end
        : r.completed === null))
      .sort((a, b) => b.business_days - a.business_days
        || String(a.grm_demand_number || '').localeCompare(String(b.grm_demand_number || ''))),
    [rows, phaseLabel, period]
  );
  // The list's two parts: the bar's red and blue for open requests; breached and met for completed ones.
  const flagged = period ? (r) => r.result === 'Missed' : isOverTarget;
  const fine = period ? (r) => r.result === 'Met' : (r) => !isOverTarget(r);
  const filterLabels = { all: 'All', flagged: period ? 'Breached' : 'Over target', fine: 'Within target' };
  const counts = { all: listRows.length, flagged: listRows.filter(flagged).length, fine: listRows.filter(fine).length };
  const needle = query.trim().toLowerCase();
  const listed = listRows.filter((r) => (show === 'all' || (show === 'flagged' ? flagged(r) : fine(r)))
    && (!needle || `${r.grm_demand_number || ''} ${r.title || ''}${allPhases ? ` ${r.phase}` : ''}`.toLowerCase().includes(needle)));
  const scope = { flagged: period ? 'breached ' : 'over-target ', fine: 'within-target ' }[show] || '';
  let noun = period ? 'tickets' : 'requests';
  if (allPhases) noun = 'phases';
  let emptyText = scope ? `No ${scope}${noun} here.` : `No ${period ? noun : 'open requests'} here.`;
  if (needle) emptyText = `No ${scope}demand matches "${query.trim()}".`;
  let heading = `${phaseLabel} — ${plural(listRows.length, 'open request', 'open requests')}`;
  let subheading = `Longest time in ${phaseLabel} first · business days, measured to ${asOfLabel ? `the end of ${asOfLabel}` : 'now'}`;
  let backLabel = `Back to the ${phaseLabel} list`;
  if (period && allPhases) {
    heading = `${plural(listRows.length, 'phase', 'phases')} completed ${periodPhrase(granularity, period.label)}`;
    subheading = "Longest first · business days each phase took, against its request type's own target";
    backLabel = `Back to the list for ${period.label}`;
  } else if (period) {
    heading = `${phaseLabel} — ${plural(listRows.length, 'ticket', 'tickets')} completed ${periodPhrase(granularity, period.label)}`;
    subheading = `Longest first · business days from starting ${phaseLabel} to completing it`;
    backLabel = `Back to the ${phaseLabel} list for ${period.label}`;
  }
  // With every request type in view the chart's target line is their average, while
  // each ticket here is judged against its own type's target -- say so when they differ.
  const blendedTarget = Boolean(period) && chartTarget !== null && chartTarget !== undefined
    && listRows.some((r) => r.target_business_days !== null && r.target_business_days !== undefined
      && Math.abs(r.target_business_days - chartTarget) > 1e-9);
  const rowKey = (r) => `${r.request_id}-${r.phase}`;
  const onView = (r) => {
    lastViewed.current = rowKey(r);
    setViewing(r);
  };

  return (
    // The dialog is named by its heading alone, never by the buttons beside it.
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby={headingId}>
      <DialogTitle id={`${headingId}title`} sx={{ pr: 6 }}>
        {viewing ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton ref={backRef} size="small" onClick={() => setViewing(null)} aria-label={backLabel}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography id={headingId} variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {viewing.grm_demand_number || `Request ${viewing.request_id}`}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5b6573', overflowWrap: 'anywhere' }}>{viewing.title}</Typography>
            </Box>
          </Stack>
        ) : (
          <>
            <Typography id={headingId} variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {heading}
            </Typography>
            <Typography variant="body2" sx={{ color: '#5b6573' }}>{subheading}</Typography>
            {blendedTarget && (
              <Typography variant="caption" component="div" sx={{ color: '#6b7280', mt: 0.25 }}>
                {`Each ticket is judged against its own request type's target. The chart's Target SLA (${daysText(chartTarget)}) is the average across types.`}
              </Typography>
            )}
          </>
        )}
      </DialogTitle>
      <IconButton onClick={onClose} aria-label="Close" sx={{ position: 'absolute', right: 12, top: 12 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        {viewing ? (
          <>
            <RequestView row={viewing} rows={rows} asOfLabel={asOfLabel} canFetch={isAdmin} />
            {viewing.grm_demand_number && (
              <Button
                component="a"
                href={trackRequestsUrl(viewing.grm_demand_number)}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ mt: 2, textTransform: 'none', color: ALLIANZ_BLUE }}
              >
                Open in Track Requests
              </Button>
            )}
          </>
        ) : (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
              <TextField
                size="small"
                placeholder="Search demand number or title"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus={lastViewed.current === null}
                sx={{ flex: 1 }}
                slotProps={{
                  input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> },
                  htmlInput: { 'aria-label': 'Search demand number or title' },
                }}
              />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={show}
                onChange={(_e, value) => { if (value) setShow(value); }}
                aria-label="Show"
              >
                {['all', 'flagged', 'fine'].map((key) => (
                  <ToggleButton key={key} value={key} sx={{ textTransform: 'none' }}>{`${filterLabels[key]} (${counts[key]})`}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
            <TableContainer sx={{ maxHeight: '55vh', border: '1px solid #e3e8ef', borderRadius: '8px' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Demand number', 'Title', 'Request type', ...(allPhases ? ['Phase'] : []), period ? 'Time taken' : 'Time in phase', 'Status'].map((h) => (
                      <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                    ))}
                    <TableCell sx={headCellSx}><Box component="span" sx={visuallyHidden}>Actions</Box></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listed.map((r) => (
                    <TableRow key={`${r.request_id}-${r.phase}`} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.grm_demand_number || '—'}</TableCell>
                      <TableCell sx={{ width: '100%', maxWidth: 0, minWidth: 120 }}>
                        <Typography variant="body2" noWrap title={r.title}>{r.title}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 110 }}>{r.request_type}</TableCell>
                      {allPhases && <TableCell sx={{ minWidth: 110 }}>{r.phase}</TableCell>}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{daysText(r.business_days)}</Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {period ? `completed ${stampDate(r.completed)}` : `since ${stampDate(r.started)}`}
                        </Typography>
                      </TableCell>
                      <TableCell><RowStatus row={r} /></TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => onView(r)}
                          ref={(el) => { viewButtons.current[rowKey(r)] = el; }}
                          aria-label={`View ${r.grm_demand_number || `request ${r.request_id}`}${allPhases ? ` ${r.phase}` : ''}`}
                          sx={{ textTransform: 'none', color: ALLIANZ_BLUE, borderColor: ALLIANZ_BLUE }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!listed.length && (
                <Typography variant="body2" sx={{ color: '#6b7280', p: 2, textAlign: 'center' }}>{emptyText}</Typography>
              )}
            </TableContainer>
            {/* A status region, so a screen reader hears how many match as the search changes. */}
            <Typography role="status" variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 1 }}>
              {listed.length ? `Showing ${listed.length} of ${listRows.length}` : `${emptyText} 0 of ${listRows.length} shown`}
            </Typography>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// What fits the 118px phase axis at 12px; a longer name wraps at a space.
const PHASE_TICK_MAX_CHARS = 16;
function tickLines(label) {
  const lines = [];
  label.split(' ').forEach((word) => {
    const last = lines[lines.length - 1];
    if (last && `${last} ${word}`.length <= PHASE_TICK_MAX_CHARS) lines[lines.length - 1] = `${last} ${word}`;
    else lines.push(word);
  });
  return lines;
}

/** A y-axis phase name that also opens its list, so the list is reachable by keyboard as well as by clicking a bar. */
const PhaseTick = ({ x, y, payload, textAnchor, counts, onSelect }) => {
  // Recharts can hand a tick a non-string value; the name is always text here.
  const label = String(payload.value ?? '');
  const lines = tickLines(label);
  const count = counts[label] || 0;
  const open = () => onSelect(label);
  const actions = count ? {
    role: 'button',
    tabIndex: 0,
    'aria-label': `List the ${plural(count, 'open request', 'open requests')} in ${label}`,
    style: { cursor: 'pointer' },
    onClick: open,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    },
  } : {};
  return (
    <text x={x} y={y} textAnchor={textAnchor} fontSize={12} fill="#374151" className="sla-phase-tick" {...actions}>
      {/* Lines centred on the bar: the first raised by half a line per extra line. */}
      {lines.map((line, i) => (
        <tspan key={line} x={x} dy={i === 0 ? `${0.355 - 0.6 * (lines.length - 1)}em` : '1.2em'}>{line}</tspan>
      ))}
    </text>
  );
};

/**
 * Where open requests are sitting, split by whether they have already passed
 * their phase target: now, or at the end of a range that ended earlier
 * (asOfLabel set). Clicking a phase's bar, or its name, lists its requests.
 */
const OpenRequestsTile = ({ phases, rows, asOfLabel }) => {
  // phase stays while the dialog fades out; key starts every opening afresh.
  const [list, setList] = useState({ open: false, phase: null, key: 0 });
  const data = phases.map((p) => ({
    phase_label: p.phase_label,
    within: p.in_flight - p.in_flight_over_target,
    over: p.in_flight_over_target,
    total: p.in_flight,
    oldest: p.in_flight_oldest_business_days,
  }));
  const total = data.reduce((sum, d) => sum + d.total, 0);
  const oldest = data.reduce((best, d) => (d.oldest !== null && (!best || d.oldest > best.oldest) ? d : best), null);
  const counts = Object.fromEntries(data.map((d) => [d.phase_label, d.total]));
  const openList = (label) => {
    if (label && counts[label]) setList((current) => ({ open: true, phase: label, key: current.key + 1 }));
  };
  // Either part of a bar opens the whole phase; recharts hands over the clicked segment.
  const onBarClick = (segment) => openList(segment && segment.payload && segment.payload.phase_label);
  // So does a click anywhere on the phase's highlighted row, not just its painted bar.
  const onChartClick = (state) => { if (state && state.isTooltipActive) openList(state.activeLabel); };
  return (
    <Paper elevation={0} sx={tileSx}>
      <TileHeader
        title={asOfLabel ? `Open requests on ${asOfLabel}` : 'Open requests — where they are now'}
        subtitle={(
          <>
            {asOfLabel ? 'Requests in each phase at the end of the range' : 'Requests currently in each phase'}
            {total ? <Box component="span" sx={{ whiteSpace: 'nowrap' }}> · click a bar to list them</Box> : null}
          </>
        )}
      />
      <Box sx={{ position: 'relative', height: 190 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 4, right: 28, bottom: 0, left: 8 }}
            barCategoryGap="28%"
            onClick={onChartClick}
            accessibilityLayer={false}
            style={{ cursor: total ? 'pointer' : 'default' }}
          >
            <CartesianGrid stroke={GRID_COLOR} horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#5b6573' }} axisLine={{ stroke: '#d0d5dd' }} tickLine={false} />
            <YAxis type="category" dataKey="phase_label" width={118} tick={<PhaseTick counts={counts} onSelect={openList} />} axisLine={false} tickLine={false} />
            <ChartTooltip
              content={<OpenRequestsTooltip />}
              cursor={{ fill: '#f2f4f7' }}
              wrapperStyle={{ zIndex: 10 }}
            />
            <Bar dataKey="within" stackId="a" fill={MEASURED_COLOR} isAnimationActive={false} cursor="pointer" onClick={onBarClick} />
            <Bar dataKey="over" stackId="a" fill={OVER_COLOR} radius={[0, 4, 4, 0]} isAnimationActive={false} cursor="pointer" onClick={onBarClick}>
              <LabelList dataKey="total" position="right" style={{ fontSize: 12, fill: '#374151', fontWeight: 600 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {total === 0 && <EmptyOverlay text="No open requests" />}
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
        <LegendItem color={MEASURED_COLOR} label="Within target" />
        <LegendItem color={OVER_COLOR} label="Over target" />
        {oldest && (
          <Typography variant="caption" sx={{ color: '#5b6573' }}>
            Longest wait: {formatDays(oldest.oldest)} business days in {oldest.phase_label}
          </Typography>
        )}
      </Stack>
      <RequestListDialog
        open={list.open}
        phaseLabel={list.phase}
        openKey={list.key}
        rows={rows}
        asOfLabel={asOfLabel}
        onClose={() => setList((current) => ({ ...current, open: false }))}
      />
    </Paper>
  );
};

// Data View's table, and its XLSX, column by column.
const DATA_VIEW_COLUMNS = [
  { key: 'grm_demand_number', label: 'Demand number' },
  { key: 'title', label: 'Title' },
  { key: 'request_type', label: 'Request type' },
  { key: 'phase', label: 'Phase' },
  { key: 'started', label: 'Started (UTC)' },
  { key: 'completed', label: 'Completed (UTC)' },
  { key: 'business_days', label: 'Business days' },
  { key: 'target_business_days', label: 'Target (business days)' },
  { key: 'result', label: 'Result' },
];

const SlaTrackerContent = () => {
  const [variant, setVariant] = useState('soft');
  // '' = all tickets, 'closed' | 'open'
  const [status, setStatus] = useState('');
  const [selectedDemandNumbers, setSelectedDemandNumbers] = useState([]);
  const [demandNumberOptions, setDemandNumberOptions] = useState([]);
  const [data, setData] = useState(null);
  // The status and demand-number filters behind `data`, for its XLSX: the payload doesn't echo them.
  const [dataFilters, setDataFilters] = useState({ status: '', demandNumbers: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dataViewOpen, setDataViewOpen] = useState(false);
  const [rangePreset, setRangePreset] = useState('all');
  // customRange: the From/To fields as typed. appliedCustom: the last usable
  // custom range, which stays on screen while the fields are half-typed.
  const [customRange, setCustomRange] = useState({ start_date: '', end_date: '' });
  const [appliedCustom, setAppliedCustom] = useState({});
  const [refreshTick, setRefreshTick] = useState(0);
  // Read every render, so a tab left open past midnight moves on with the day.
  const todayIso = currentIsoDate();
  // Only the latest request may land: a date typed digit by digit fires several.
  const latestFetch = useRef(0);

  const demandNumberFilter = useMemo(
    () => selectedDemandNumbers.map((opt) => opt.grm_demand_number),
    [selectedDemandNumbers]
  );

  // The range requested; '' on both ends = all data.
  const { start_date: rangeStart = '', end_date: rangeEnd = '' } = rangePreset === 'custom'
    ? appliedCustom
    : presetParams(rangePreset, parseIsoDate(todayIso));
  // Keyed on the dates, so switching to a custom range with the same dates refetches nothing.
  const rangeParams = useMemo(
    () => (rangeStart ? { start_date: rangeStart, end_date: rangeEnd } : {}),
    [rangeStart, rangeEnd]
  );
  const customComplete = isUsableDate(customRange.start_date) && isUsableDate(customRange.end_date);
  const customIncomplete = rangePreset === 'custom' && !customComplete;
  const customRangeInvalid = rangePreset === 'custom' && customComplete && customRange.start_date > customRange.end_date;

  const fetchData = useCallback(async () => {
    const fetchId = ++latestFetch.current;
    setLoading(true);
    setError('');
    try {
      const result = await DashboardAnalyticsService.getSlaDashboard({
        variant, status, grm_demand_number: demandNumberFilter, ...rangeParams,
      });
      if (fetchId === latestFetch.current) {
        setData(result);
        setDataFilters({ status, demandNumbers: demandNumberFilter });
      }
    } catch (err) {
      if (fetchId !== latestFetch.current) return;
      console.error('SLA dashboard fetch failed', err);
      setError('Failed to load SLA data.');
    } finally {
      if (fetchId === latestFetch.current) setLoading(false);
    }
  }, [variant, status, demandNumberFilter, rangeParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTick]);

  useEffect(() => {
    DashboardAnalyticsService.getBandwidthDemandNumbers({ status })
      .then(setDemandNumberOptions)
      .catch((err) => {
        console.error('Failed to load demand-number options', err);
        setDemandNumberOptions([]);
      });
  }, [status]);

  // 'month' | 'week' | 'day' -- also the noun the captions use.
  const granularity = data?.granularity || 'month';
  const periods = useMemo(() => data?.periods || [], [data]);
  const periodKeys = useMemo(() => periods.map((p) => p.key), [periods]);
  const labels = useMemo(() => periodLabeller(periods, granularity), [periods, granularity]);
  const shown = useMemo(() => shownPayload(data), [data]);
  const phases = useMemo(() => shown?.phases || [], [shown]);
  const kpis = shown?.kpis || {};
  const variantCounts = data?.variant_counts || {};
  const rangeText = data ? formatDateRange(data.start_date, data.end_date) : '';
  const asOfLabel = data && !data.in_flight_is_now ? formatDateRange(data.end_date, data.end_date) : null;

  // Only PROD has a tracking start; UAT and local count every request.
  const beforeTracking = Boolean(data?.metrics_start && rangeStart && rangeStart < data.metrics_start);
  const coverage = useMemo(() => requestCoverage(data, shown), [data, shown]);
  const [coverageHead, coverageRest] = coverageSentence(coverage, data?.variant_label, data?.metrics_start);
  const usableRange = (range) => isUsableDate(range.start_date) && isUsableDate(range.end_date)
    && range.start_date <= range.end_date;
  // From/To only exist for a custom range; they open on the range already on
  // screen, which stays applied until both dates are usable.
  const onPresetChange = (key) => {
    if (key === 'custom') {
      const seed = { start_date: rangeStart || data?.start_date || '', end_date: rangeEnd || data?.end_date || '' };
      setCustomRange(seed);
      setAppliedCustom(usableRange(seed) ? seed : rangeParams);
    }
    setRangePreset(key);
  };
  const onDateChange = (field) => (e) => {
    const next = { ...customRange, [field]: e.target.value };
    setCustomRange(next);
    if (usableRange(next)) setAppliedCustom(next);
  };
  const completedByPeriod = useMemo(
    () => periodKeys.map((_key, i) => phases.reduce((sum, p) => sum + (p.series[i]?.completed || 0), 0)),
    [periodKeys, phases]
  );
  const completedTotal = phases.reduce((sum, p) => sum + p.completed, 0);
  // Every tile in the ticket view: the line's colours are explained once, in the shared legend.
  const allTicketView = phases.every((p) => !DAYS_VIEW_PHASES.has(p.phase_key));

  const variantLabel = (opt) => {
    const count = variantCounts[opt.key];
    return count === undefined || !data ? opt.label : `${opt.label} (${count})`;
  };

  const saved = kpis.sla_current_days !== null && kpis.sla_automated_days !== null && kpis.sla_current_days > 0
    ? kpis.sla_current_days - kpis.sla_automated_days
    : null;

  // ---- Data View: per-request rows behind every number on the page ----------
  const dataViewRows = useMemo(
    () => (shown?.rows || []).map((r) => ({
      ...r,
      started: r.started ? r.started.slice(0, 16).replace('T', ' ') : '',
      completed: r.completed ? r.completed.slice(0, 16).replace('T', ' ') : '',
    })),
    [shown]
  );
  // The XLSX holds exactly the table -- its columns, in its order, under its headers --
  // and an About sheet saying what the rows are, since the file travels without the page.
  const dataViewExport = useMemo(
    () => dataViewRows.map((r) => Object.fromEntries(DATA_VIEW_COLUMNS.map((c) => [c.label, r[c.key] ?? '']))),
    [dataViewRows]
  );
  const dataViewAbout = data ? [
    { Item: 'Request type', Value: data.variant_label },
    ...(coverageHead ? [{ Item: 'Requests', Value: `${coverageHead}: ${coverageRest}` }] : []),
    { Item: 'Phases', Value: phases.map((p) => p.phase_label).join(', ') },
    { Item: 'Date range', Value: rangeText },
    { Item: 'Ticket status', Value: { closed: 'Closed', open: 'Open' }[dataFilters.status] || 'All' },
    { Item: 'Demand numbers', Value: dataFilters.demandNumbers.length ? dataFilters.demandNumbers.join(', ') : 'All' },
    { Item: 'Open requests as of', Value: asOfLabel ? `the end of ${asOfLabel}` : stampTime(data.in_flight_as_of) },
    { Item: 'Units', Value: 'Business days (Monday to Friday); times in UTC' },
  ] : [];

  return (
    <Box p={3} sx={{ background: '#f7f8fa' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} mb={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            Bandwidth SLA Tracker
          </Typography>
          <Typography variant="body2" sx={{ color: '#5b6573', mt: 0.5 }}>
            Time each phase takes now, against the target agreed for automation. All figures in business days.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={() => setRefreshTick((n) => n + 1)} size="small" sx={{ color: ALLIANZ_BLUE }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDataViewOpen(true)}
            disabled={!data}
            sx={{ textTransform: 'none', color: ALLIANZ_BLUE, borderColor: ALLIANZ_BLUE, background: '#fff' }}
          >
            Data View
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap mb={2.5}>
        <FormControl size="small" sx={{ minWidth: 160, background: '#fff' }}>
          <InputLabel id="sla-range-label">Date range</InputLabel>
          <Select labelId="sla-range-label" value={rangePreset} label="Date range" onChange={(e) => onPresetChange(e.target.value)}>
            {RANGE_PRESETS.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {rangePreset === 'custom' && (
          <>
            <TextField
              type="date"
              label="From"
              size="small"
              value={customRange.start_date}
              onChange={onDateChange('start_date')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: data?.metrics_start || undefined, max: customRange.end_date || todayIso }}
              sx={{ width: 160, background: '#fff' }}
            />
            <TextField
              type="date"
              label="To"
              size="small"
              value={customRange.end_date}
              onChange={onDateChange('end_date')}
              error={customRangeInvalid}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: customRange.start_date || data?.metrics_start || undefined, max: todayIso }}
              sx={{ width: 160, background: '#fff' }}
            />
          </>
        )}
        <FormControl size="small" sx={{ minWidth: 220, background: '#fff' }}>
          <InputLabel id="sla-variant-label">Request type</InputLabel>
          <Select labelId="sla-variant-label" value={variant} label="Request type" onChange={(e) => setVariant(e.target.value)}>
            {VARIANT_OPTIONS.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>{variantLabel(opt)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130, background: '#fff' }}>
          <InputLabel id="sla-status-label" shrink>Ticket status</InputLabel>
          <Select labelId="sla-status-label" value={status} label="Ticket status" displayEmpty onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
            <MenuItem value="open">Open</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          multiple
          size="small"
          sx={{ minWidth: 260, background: '#fff' }}
          options={demandNumberOptions}
          value={selectedDemandNumbers}
          onChange={(_e, value) => setSelectedDemandNumbers(value)}
          getOptionLabel={(opt) => `${opt.grm_demand_number} — ${opt.title || 'untitled'}`}
          isOptionEqualToValue={(opt, val) => opt.request_id === val.request_id}
          renderInput={(params) => <TextField {...params} label="Demand number" placeholder="Search…" />}
        />
        {customRangeInvalid && (
          <Typography variant="caption" sx={{ color: OVER_COLOR, fontWeight: 600 }}>
            From must be on or before To
          </Typography>
        )}
        {customIncomplete && (
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            Enter both dates to apply the range
          </Typography>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={32} sx={{ color: ALLIANZ_BLUE }} />
        </Box>
      ) : data && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          {/* Every request of the type, accounted for */}
          {coverageHead && (
            <Typography variant="body2" sx={{ color: '#374151', mb: 1.5 }}>
              <Box component="span" sx={{ fontWeight: 700 }}>{coverageHead}</Box>
              {`: ${coverageRest}`}
            </Typography>
          )}
          {/* Headline numbers */}
          <Box sx={{ display: 'grid', gap: 2, mb: 2.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' } }}>
            <KpiCard
              label="End-to-end SLA before automation"
              value={formatDays(kpis.sla_current_days)}
              unit="days"
              caption="Client SLA tracker, manual process"
            />
            <KpiCard
              label="End-to-end target SLA"
              value={formatDays(kpis.sla_automated_days)}
              unit="days"
              caption={saved !== null
                ? `Target is ${formatDays(saved)} days (${Math.round((100 * saved) / kpis.sla_current_days)}%) shorter than before automation`
                : 'Agreed automated SLA'}
            />
            <KpiCard
              label="SLA compliance"
              value={kpis.compliance_pct === null ? '—' : `${Math.round(kpis.compliance_pct)}%`}
              caption={kpis.compliance_scored
                ? `${kpis.compliance_met} of ${kpis.compliance_scored} completed phases within target`
                  + (kpis.latest_period ? ` · ${labels.full(kpis.latest_period)}: ${kpis.latest_met} of ${kpis.latest_scored}` : '')
                : completedTotal ? 'No completed phase had a target in this range' : 'No phases completed in this range'}
            />
            <KpiCard
              label={asOfLabel ? `Open requests on ${asOfLabel}` : 'Open requests'}
              value={kpis.in_flight}
              alert={kpis.in_flight_over_target > 0}
              caption={kpis.in_flight
                ? `${kpis.in_flight_over_target} already over their phase target`
                : 'Nothing in progress'}
            />
          </Box>

          {/* Shared legend for the four phase charts, and how they are grouped */}
          <Stack direction="row" alignItems="center" sx={{ mb: 1.5, px: 0.5, columnGap: 3, rowGap: 0.75 }} flexWrap="wrap" useFlexGap>
            <LegendItem
              color={MEASURED_COLOR}
              swatch={allTicketView ? <StatusLineSwatch /> : null}
              label={`${ACTUAL_LABEL} (median of that ${granularity}'s completions)`}
            />
            {allTicketView && [['below', 'Within target'], ['above', 'Over target']].map(([status, label]) => (
              <LegendItem key={status} swatch={<StatusShape status={status} />} label={label} />
            ))}
            <LegendItem color={TARGET_COLOR} dash={TARGET_DASH} label={TARGET_LABEL} />
            <LegendItem color={BEFORE_COLOR} dash={BEFORE_DASH} label={BEFORE_LABEL} />
            <Typography variant="caption" sx={{ color: '#6b7280' }}>
              Hover a chart to see that {granularity}'s figures, click a point for its tickets
            </Typography>
            <Typography variant="caption" sx={{ color: '#374151', ml: 'auto !important' }}>
              <Box component="span" sx={{ fontWeight: 700 }}>{GRANULARITY_LABEL[granularity]}</Box>
              {` · ${rangeText}`}
              {beforeTracking ? ` · tracking starts ${formatDateRange(data.metrics_start, data.metrics_start)}` : ''}
            </Typography>
          </Stack>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' } }}>
            {phases.map((phase) => (
              <PhaseTrendTile
                key={phase.phase_key}
                phase={phase}
                periodKeys={periodKeys}
                periods={periods}
                rows={shown.rows || []}
                labels={labels}
                granularity={granularity}
                asOfLabel={asOfLabel}
                showStatusLegend={!allTicketView}
              />
            ))}
            <ComplianceTile
              compliance={shown.compliance_by_period || []}
              completed={completedByPeriod}
              labels={labels}
              noun={granularity}
              periods={periods}
              rows={shown.rows || []}
              asOfLabel={asOfLabel}
            />
            <OpenRequestsTile phases={phases} rows={shown.rows || []} asOfLabel={asOfLabel} />
          </Box>
        </Box>
      )}

      <ChartDetailModal
        open={dataViewOpen}
        onClose={() => setDataViewOpen(false)}
        title="Bandwidth SLA Tracker"
        subtitle={`Every phase behind the figures above — ${data?.variant_label || ''}${rangeText ? ` · ${rangeText}` : ''}`
          + (asOfLabel ? ` · open requests as of ${asOfLabel}` : '')}
        accentColor={ALLIANZ_BLUE}
        showChart={false}
        tableData={dataViewRows}
        tableColumns={DATA_VIEW_COLUMNS}
        rawData={dataViewExport}
        exportFormats={['xlsx']}
        exportName={data ? `bandwidth-sla-tracker_${data.variant}_${data.start_date}_to_${data.end_date}` : 'bandwidth-sla-tracker'}
        extraSheets={[{ name: 'About', rows: dataViewAbout }]}
      />
    </Box>
  );
};

export default SlaTrackerContent;
